import { Component, Input, Output, EventEmitter, HostBinding } from '@angular/core';

export type ButtonVariant = 'flat' | 'raised' | 'stroked' | 'icon' | 'text';
export type ButtonSize = 'sm' | 'md' | 'lg';
/**
 * Named design-system color themes:
 * - dark     → #111827 bg / white text  (Save, Upload, Confirm Import, Add Document)
 * - purple   → #7239ea bg / white text  (AI/wizard confirm steps)
 * - neutral  → #e4e6ef bg / dark text   (tertiary flat actions: Create Empty Template)
 * - danger   → #dc2626 bg / white text  (destructive confirm: Delete)
 * - muted    → white bg / gray border / dark text  (Cancel, Download Template — stroked secondary)
 * - orange   → #F8771A bg / white text  (warning/soft-failure actions: Reprocess)
 * - light    → white bg / dark text + border (light/neutral icon or surface buttons)
 * - success  → soft green bg / green text + border (confirmed/approved state: Approved)
 * - danger-soft → soft red bg / red text + border (rejected/corrected state: Relearned)
 */
export type ButtonTheme = 'dark' | 'purple' | 'neutral' | 'danger' | 'muted' | 'orange' | 'light' | 'success' | 'danger-soft';

@Component({
  selector: 'kt-button-v1',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss']
})
export class ButtonComponent {
  @Input() label = '';
  @Input() icon = '';
  @Input() iconPosition: 'prefix' | 'suffix' = 'prefix';
  @Input() variant: ButtonVariant = 'flat';
  @Input() size: ButtonSize = 'md';
  @Input() theme: ButtonTheme | null = null;
  @Input() tooltip: string | null = null;
  @Input() bordered = false;
  @Input() disabled = false;
  @Input() loading = false;
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  /** Spins the icon in place (e.g. a "cached"/refresh icon while a background action is running). */
  @Input() spinIcon = false;
  @Input() color = 'primary';
  @Input() fullWidth = false;
  /** When > 0, shows a count badge on the top-right corner of the button. */
  @Input() badge: number = 0;

  @HostBinding('class.pkt-btn-host--full') get hostFull() { return this.fullWidth; }
  @HostBinding('class.pkt-btn-host--badged') get hostBadged() { return this.badge > 0; }

  get themeClass() { return this.theme ? `pkt-btn--theme-${this.theme}` : ''; }
  /** Falls back to label so every button has a tooltip unless explicitly suppressed with tooltip="". */
  get effectiveTooltip() { return this.tooltip ?? this.label; }

  @Output() btnClick = new EventEmitter<MouseEvent>();

  onClick(event: MouseEvent) {
    if (!this.disabled && !this.loading) {
      this.btnClick.emit(event);
    }
  }
}
